import { Component, OnInit, signal } from "@angular/core";
import { MatDialog } from "@angular/material/dialog";
import { NavigationEnd, Router } from "@angular/router";
import { ValueChangedEvent } from "devextreme/ui/text_box";
import { BehaviorSubject, combineLatest, filter, map, of, Subject, switchMap } from "rxjs";
import { AuthService } from "src/app/auth/auth.service";
import { NewsNavbarViewModel } from "src/app/core/model/DemoNews";
import { NewsPopupComponent } from "src/app/news/news-popup/news-popup.component";
import { DemoNewsService } from "src/app/services/demo-news.service";
import { UserService } from "src/app/services/user.service";
import { MaintenancePopupComponent } from "../maintenance-popup/maintenance-popup.component";
import { MaintenanceViewModel } from "src/app/core";
import { MaintenanceService } from "src/app/services/maintenance.service";

@Component({
  selector: "brd-navbar",
  templateUrl: "./navbar.component.html",
  styleUrl: "./navbar.component.less",
  standalone: false
})
export class NavbarComponent implements OnInit {
  viewButtonSuperUser: boolean = false;

  showFirstSubmenuModes = {
    name: "onHover",
    delay: { show: 0, hide: 500 }
  };

  navItems = [];
  routerService: Router;

  private allNews = [];
  badgeObs = this.newsService.newsToRead$.pipe(
    map(news => {
      this.allNews = news;
      return news;
    })
  );

  private searchSubject = new BehaviorSubject<string>("");
  search$ = this.searchSubject.asObservable();

  team$ = combineLatest([this.userService.team$, this.search$]).pipe(
    map(([team, search]) => {
      if (!team || !team.users) {
        return [];
      }

      this.teamName.set(team.teamName);
      return team.users
        .filter(x => x.name.toLowerCase().includes(search.toLowerCase()) || x.userName.toLowerCase().includes(search.toLowerCase()))
        .map(x => ({
          ...x,
          iconText: x.userName.substring(0, 2).toUpperCase()
        }));
    })
  );

  protected searchButtonOptions = {
    icon: "search",
    stylingMode: "text",
    type: "noraml",
    hoverStateEnabled: false,
    activeStateEnabled: false,
    elementAttr: {
      class: "team-search-icon"
    }
  };

  expanded = false;
  userMenuVisible = false;
  newsMenuVisible = signal(false);

  teamName = signal<string>("");
  maintenanceScheduleDate = signal<Date>(null);
  maintenance$ = this.maintenanceService.maintenance$.pipe(
    map(maintenance => {
      if (maintenance.id) this.maintenanceScheduleDate.set(new Date(maintenance.startDate));
      else this.maintenanceScheduleDate.set(null);

      return maintenance;
    })
  );

  constructor(
    private userService: UserService,
    private authService: AuthService,
    private newsService: DemoNewsService,
    private maintenanceService: MaintenanceService,
    private readonly dialog: MatDialog,
    router: Router
  ) {
    this.navItems = [
      { text: "Home", link: "/" },
      { text: "News", link: "/news" },
      { text: "About", link: "/about" },
      { text: "Support", link: "/support" },
      { text: "Faq", link: "/faq" }
    ];
    this.routerService = router;

    const urlPath = window.location.pathname;
    this.newsMenuVisible.set(!urlPath.startsWith("/demo") && !urlPath.startsWith("/admin-portal") && !urlPath.startsWith("/template"));
  }

  ngOnInit(): void {
    this.userService.roles$.subscribe(roles => {
      this.viewButtonSuperUser = roles ? this.userService.isSuperUser() : false;
    });

    this.userService.getTeamMembers().subscribe();

    this.routerService.events.pipe(filter(event => event instanceof NavigationEnd)).subscribe();
  }

  signOut(): void {
    this.authService.logout();
  }

  onSearchChange(e: ValueChangedEvent): void {
    this.searchSubject.next(e.value);
  }

  seeAllNews(event: Event): void {
    event.stopPropagation();
    this.newsMenuVisible.set(false);
    this.routerService.navigate(["/news"]);
  }

  protected openNewsPopup(news: NewsNavbarViewModel, i: number): void {
    const popupInfoSubject = new Subject<{
      newsId: number;
      action: "prev" | "next";
    }>();
    const dialogRef = this.dialog.open(NewsPopupComponent, {
      data: {
        title: news.title,
        id: news.id,
        action: (currentId: number, action: "prev" | "next") => popupInfoSubject.next({ newsId: currentId, action: action }),
        infoPopup$: popupInfoSubject.asObservable().pipe(
          switchMap(({ newsId, action }) => {
            const currentIndex = this.allNews.findIndex(x => x.id === newsId);
            const newIndex = action === "prev" ? currentIndex - 1 : currentIndex + 1;

            const item = this.allNews[newIndex];
            return of({
              id: item?.id,
              title: item?.title,
              hasPrev: newIndex > 0,
              hasNext: newIndex < this.allNews.length - 1,
              publishDate: item?.lastUpdate
            });
          })
        ),
        hasPrev: i > 0,
        hasNext: i < this.allNews.length - 1,
        publishDate: news.lastUpdate,
        showGoToNews: true
      },
      position: { top: "60px", right: "120px" },
      panelClass: "news-popup",
      width: "1078px",
      height: "726px",
      maxWidth: "80vw",
      maxHeight: "80vh",
      disableClose: true
    });

    dialogRef.afterClosed().subscribe(() => {
      this.newsService.getNewsToRead().subscribe();
    });
  }

  protected openMaintenancePopup(maintenance: MaintenanceViewModel): void {
    const dialogRef = this.dialog.open(MaintenancePopupComponent, {
      data: maintenance,
      width: "650px",
      height: "400px"
    });
    dialogRef.afterClosed().subscribe();
  }
}
